/**
 * Utilities for working with the new array-based image structure
 */

export type ImageSize = "thumb" | "small" | "medium" | "large";
export type ImageFormat = "avif" | "webp" | "jpeg";

export interface ImageItem {
  id?: string;
  original: {
    url: string;
  };
  base_path?: string;
  sizes?: {
    [key in ImageSize]?: {
      width: number;
      height: number;
    };
  };
  available_formats?: ImageFormat[];
  explicit_sources?: {
    [key in ImageFormat]?: {
      [key in ImageSize]?: string;
    };
  };
}

export type ImageArray = ImageItem[];

/**
 * Get the best image URL from a variant item for a given size and format preference
 */
export function getBestImageUrl(
  imageItem: ImageItem,
  size: ImageSize = "medium",
  formatPreference: ImageFormat[] = ["avif", "webp", "jpeg"]
): string | null {
  // Check explicit sources first (for manual overrides/hashed assets)
  if (imageItem.explicit_sources) {
    for (const format of formatPreference) {
      const typeSources = imageItem.explicit_sources[format];
      if (typeSources) {
        // Try exact match
        if (typeSources[size]) {
          return typeSources[size];
        }
        // Fallback for explicit sources
        const fallbackSizes: ImageSize[] = [
          "large",
          "medium",
          "small",
          "thumb",
        ];
        for (const fallbackSize of fallbackSizes) {
          if (typeSources[fallbackSize]) {
            return typeSources[fallbackSize];
          }
        }
      }
    }
  }

  // If we have the new compact structure
  if (
    imageItem.base_path &&
    imageItem.available_formats &&
    imageItem.sizes?.[size]
  ) {
    // Check for preferred format availability
    for (const format of formatPreference) {
      if (imageItem.available_formats.includes(format)) {
        return `${imageItem.base_path}_${size}.${format}`;
      }
    }
  }

  // Fallback to other sizes if requested size not available (only for new structure check logic)
  if (imageItem.sizes && !imageItem.sizes[size]) {
    const fallbackSizes: ImageSize[] = ["large", "medium", "small", "thumb"];
    for (const fallbackSize of fallbackSizes) {
      if (fallbackSize !== size && imageItem.sizes[fallbackSize]) {
        // We found a size that exists, recurse
        return getBestImageUrl(imageItem, fallbackSize, formatPreference);
      }
    }
  }

  return imageItem.original.url;
}

/**
 * Get dimensions for a specific size variant
 */
export function getImageDimensions(
  imageItem: ImageItem,
  size: ImageSize = "medium"
): { width: number; height: number } | null {
  return imageItem.sizes?.[size] || null;
}

/**
 * Create a responsive srcset string from a variant item
 */
export function createSrcSet(
  imageItem: ImageItem,
  format: ImageFormat = "webp"
): string {
  const sizes: ImageSize[] = ["thumb", "small", "medium", "large"];
  const srcsetEntries: string[] = [];

  // Logic needs to be robust: if available_formats includes format, we can generate URLs for all existing sizes
  if (imageItem.base_path && imageItem.available_formats?.includes(format)) {
    for (const size of sizes) {
      if (imageItem.sizes?.[size]) {
        const url = `${imageItem.base_path}_${size}.${format}`;
        const width = imageItem.sizes[size]?.width;
        if (width) {
          srcsetEntries.push(`${url} ${width}w`);
        }
      }
    }
  }

  return srcsetEntries.join(", ");
}

/**
 * Generate picture element HTML with format fallbacks
 */
export function generatePictureHtml(
  imageItem: ImageItem,
  alt: string,
  sizes = "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
): string {
  const sources: string[] = [];

  // AVIF source (best compression)
  const avifSrcset = createSrcSet(imageItem, "avif");
  if (avifSrcset) {
    sources.push(
      `<source type="image/avif" srcset="${avifSrcset}" sizes="${sizes}">`
    );
  }

  // WebP source (good compression, better support)
  const webpSrcset = createSrcSet(imageItem, "webp");
  if (webpSrcset) {
    sources.push(
      `<source type="image/webp" srcset="${webpSrcset}" sizes="${sizes}">`
    );
  }

  // JPEG fallback (universal support)
  const jpegSrcset = createSrcSet(imageItem, "jpeg");
  const fallbackSrc =
    getBestImageUrl(imageItem, "medium") || imageItem.original.url;

  return `
<picture>
  ${sources.join("\n  ")}
  <img src="${fallbackSrc}" ${jpegSrcset ? `srcset="${jpegSrcset}"` : ""} sizes="${sizes}" alt="${alt}" loading="lazy" decoding="async">
</picture>`.trim();
}

/**
 * Get the first (primary) image from an array of variant items
 */
export function getPrimaryImage(
  variants: ImageArray | null | undefined
): ImageItem | null {
  if (!variants || !Array.isArray(variants)) return null;
  return variants.length > 0 ? variants[0] : null;
}

/**
 * Check if variants are in the new array format
 */
export function isArrayFormat(variants: unknown): variants is ImageArray {
  return Array.isArray(variants);
}

/**
 * Normalize variants to array format
 */
export function normalizeImages(variants: unknown): ImageArray {
  if (isArrayFormat(variants)) {
    return variants;
  }
  return []; // Simplified: We expect migration to have handled it.
}
